import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_URL = import.meta.env.VITE_API_URL || '';

// ============================================
// TYPES
// ============================================

export interface JobSubmission {
  id: string;
  company_name: string;
  company_website?: string;
  company_logo_url?: string;
  job_title: string;
  job_description: string;
  location: string;
  country_code: string;
  is_remote: number;
  job_type: string;
  salary_min?: number;
  salary_max?: number;
  salary_currency: string;
  visa_types_json?: string;
  korean_required: number;
  korean_preferred: number;
  industry?: string;
  experience_level?: string;
  apply_url?: string;
  contact_email: string;
  contact_name?: string;
  status: string;
  created_at: string;
  expires_at?: string;
}

export interface JobSubmissionInput {
  company_name: string;
  company_website?: string;
  company_logo_url?: string;
  job_title: string;
  job_description: string;
  location: string;
  country_code: string;
  is_remote?: boolean;
  job_type?: string;
  salary_min?: number;
  salary_max?: number;
  salary_currency?: string;
  visa_types?: string[];
  korean_required?: boolean;
  korean_preferred?: boolean;
  industry?: string;
  experience_level?: string;
  apply_url?: string;
  contact_email: string;
  contact_name?: string;
  submitter_email?: string;
}

interface JobsResponse {
  jobs: JobSubmission[];
  total: number;
  limit: number;
  offset: number;
}

interface JobsFilters {
  country?: string;
  visaType?: string;
  koreanPreferred?: boolean;
  remote?: boolean;
  limit?: number;
  offset?: number;
}

// Fetch approved visa-sponsoring jobs
export function useVisaSponsoringJobs(filters: JobsFilters = {}) {
  return useQuery<JobsResponse>({
    queryKey: ['visa-jobs', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.country) params.append('country', filters.country);
      if (filters.visaType) params.append('visaType', filters.visaType);
      if (filters.koreanPreferred) params.append('koreanPreferred', 'true');
      if (filters.remote) params.append('remote', 'true');
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());

      const response = await fetch(`${API_URL}/api/immigration/jobs?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch jobs');
      }
      return response.json();
    },
  });
}

// Fetch single job details
export function useJobDetail(jobId: string | null) {
  return useQuery<JobSubmission>({
    queryKey: ['visa-job', jobId],
    queryFn: async () => {
      if (!jobId) throw new Error('No job ID');
      const response = await fetch(`${API_URL}/api/immigration/jobs/${jobId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch job');
      }
      return response.json();
    },
    enabled: !!jobId,
  });
}

// Submit a new job listing
export function useSubmitJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: JobSubmissionInput) => {
      const response = await fetch(`${API_URL}/api/immigration/jobs/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to submit job');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visa-jobs'] });
    },
  });
}

// Admin: fetch pending job submissions
export function usePendingJobSubmissions(status: string = 'pending') {
  return useQuery<JobSubmission[]>({
    queryKey: ['job-submissions', status],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/immigration/jobs/submissions?status=${status}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch submissions');
      }
      return response.json();
    },
  });
}

// Admin: update job status
export function useUpdateJobStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, status, admin_notes, expires_at }: {
      jobId: string;
      status: 'approved' | 'rejected' | 'pending';
      admin_notes?: string;
      expires_at?: string;
    }) => {
      const response = await fetch(`${API_URL}/api/immigration/jobs/${jobId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ status, admin_notes, expires_at }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update job status');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['visa-jobs'] });
    },
  });
}

// ============================================
// ATTORNEY LISTINGS
// ============================================

export interface AttorneyListing {
  id: string;
  name: string;
  firm_name?: string;
  email: string;
  phone?: string;
  website?: string;
  photo_url?: string;
  bio?: string;
  specialties_json?: string;
  countries_json?: string;
  languages_json?: string;
  years_experience?: number;
  consultation_fee?: string;
  consultation_free: number;
  location: string;
  country_code: string;
  is_verified: number;
  is_featured: number;
  listing_type: string;
  status: string;
  created_at: string;
  expires_at?: string;
}

export interface AttorneySubmissionInput {
  name: string;
  firm_name?: string;
  email: string;
  phone?: string;
  website?: string;
  photo_url?: string;
  bio?: string;
  specialties?: string[];
  countries?: string[];
  languages?: string[];
  years_experience?: number;
  consultation_fee?: string;
  consultation_free?: boolean;
  location: string;
  country_code: string;
  listing_type?: 'basic' | 'featured';
}

interface AttorneysResponse {
  attorneys: AttorneyListing[];
  total: number;
  limit: number;
  offset: number;
}

interface AttorneysFilters {
  country?: string;
  specialty?: string;
  limit?: number;
  offset?: number;
}

// Fetch approved attorneys
export function useAttorneys(filters: AttorneysFilters = {}) {
  return useQuery<AttorneysResponse>({
    queryKey: ['attorneys', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.country) params.append('country', filters.country);
      if (filters.specialty) params.append('specialty', filters.specialty);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());

      const response = await fetch(`${API_URL}/api/immigration/attorneys?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch attorneys');
      }
      return response.json();
    },
  });
}

// Submit attorney listing
export function useSubmitAttorney() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AttorneySubmissionInput) => {
      const response = await fetch(`${API_URL}/api/immigration/attorneys/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to submit listing');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attorneys'] });
    },
  });
}

// Fetch listing pricing
export function useListingPricing() {
  return useQuery({
    queryKey: ['listing-pricing'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/immigration/pricing`);
      if (!response.ok) {
        throw new Error('Failed to fetch pricing');
      }
      return response.json();
    },
  });
}
